import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  formatError,
  formatHeading,
  normalizeDeg,
  solve,
  type CompassError,
  type CompassSolution,
  type ReferenceFrame,
} from "@nav-games/compass";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface ConvertProps {
  onNavigate: (to: string) => void;
}

const MODULE = "compass";
const GAME = "convert";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  /// "c2t" = convert compass → true, "t2c" = true → compass.
  direction: "c2t" | "t2c";
  knownFrame: ReferenceFrame;
  knownValue: number;
  variation: CompassError;
  deviation: CompassError;
  solution: CompassSolution;
  /// Normalised degrees of the correct answer.
  target: number;
  /// Heading strings ("145°") — one is the target.
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function randError(minDeg: number, maxDeg: number): CompassError {
  return {
    degrees: randInt(minDeg, maxDeg),
    hand: Math.random() < 0.5 ? "E" : "W",
  };
}

function buildChoices(target: number, solution: Card["solution"], v: CompassError, d: CompassError): string[] {
  // Distractors are realistic sign mistakes: flipping a correction's
  // sign shifts the answer by twice that error; stopping at magnetic
  // is the "forgot the second step" error.
  const pool = [
    normalizeDeg(target + 2 * v.degrees),
    normalizeDeg(target - 2 * v.degrees),
    normalizeDeg(target + 2 * d.degrees),
    normalizeDeg(target - 2 * d.degrees),
    solution.magneticDeg,
  ];
  const chosen: number[] = [];
  const seen = new Set<number>([target]);
  for (const cand of pool) {
    if (chosen.length >= CHOICES_PER_CARD - 1) break;
    if (!seen.has(cand)) {
      seen.add(cand);
      chosen.push(cand);
    }
  }
  // Top up with small offsets if collisions left us short.
  let off = 4;
  while (chosen.length < CHOICES_PER_CARD - 1) {
    for (const cand of [normalizeDeg(target + off), normalizeDeg(target - off)]) {
      if (chosen.length >= CHOICES_PER_CARD - 1) break;
      if (!seen.has(cand)) {
        seen.add(cand);
        chosen.push(cand);
      }
    }
    off += 3;
  }
  const all = [target, ...chosen].map((n) => `${formatHeading(n)}°`);
  // Fisher-Yates shuffle.
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = all[i]!;
    all[i] = all[j]!;
    all[j] = ai;
  }
  return all;
}

function nextCard(): Card {
  const direction: Card["direction"] = Math.random() < 0.5 ? "c2t" : "t2c";
  const knownFrame: ReferenceFrame = direction === "c2t" ? "compass" : "true";
  const knownValue = randInt(0, 359);
  const variation = randError(2, 11);
  const deviation = randError(1, 7);
  const solution = solve(knownFrame, knownValue, variation, deviation);
  const target = direction === "c2t" ? solution.trueDeg : solution.compassDeg;
  return {
    direction,
    knownFrame,
    knownValue,
    variation,
    deviation,
    solution,
    target,
    choices: buildChoices(target, solution, variation, deviation),
  };
}

export function CompassConvert({ onNavigate }: ConvertProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => nextCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(nextCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === `${formatHeading(card.target)}°`;
      const attempt: Omit<Attempt, "module"> = {
        char: card.direction,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(nextCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/chartwork")}
      />
    );
  }

  const revealed = chosen !== null;
  const targetLabel = card.direction === "c2t" ? "True" : "Compass";
  const correctStr = `${formatHeading(card.target)}°`;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "compass" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>
          {card.direction === "c2t" ? "Compass → True" : "True → Compass"}
        </h3>
        <p style={{ marginBottom: 12 }}>
          <strong>{card.knownFrame === "compass" ? "Compass" : "True"}</strong>{" "}
          {formatHeading(card.knownValue)}°, Variation{" "}
          <strong>{formatError(card.variation)}</strong>, Deviation{" "}
          <strong>{formatError(card.deviation)}</strong>.
          <br />
          What is the <strong>{targetLabel}</strong> heading?
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
          }}
        >
          {card.choices.map((c) => {
            const isCorrect = c === correctStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 20,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: 12,
              borderRadius: 6,
              background:
                chosen === correctStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === correctStr
                ? `✓ Correct — ${correctStr} ${targetLabel}.`
                : `✗ Not quite — the answer is ${correctStr} ${targetLabel}.`}
            </p>
            <ol style={{ margin: "10px 0 0", paddingLeft: 18 }}>
              {card.solution.steps.map((s, i) => (
                <li key={i} className="small" style={{ marginBottom: 4 }}>
                  {cap(s.from)} {formatHeading(s.fromValue)}° {s.operation}{" "}
                  {s.error.degrees}° = {cap(s.to)} {formatHeading(s.toValue)}°{" "}
                  <span className="muted">
                    ({s.errorName} {formatError(s.error)})
                  </span>
                </li>
              ))}
            </ol>
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              CADET — Compass ADd East → True. Error East, compass least;
              error West, compass best.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next question →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/chartwork")}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function cap(frame: ReferenceFrame): string {
  return frame === "compass" ? "Compass" : frame === "magnetic" ? "Magnetic" : "True";
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Chartwork
        </button>
      </div>
    </>
  );
}
